import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { fetchAdmin, postAdmin, safeJsonParse, toErrorMessage } from '../../admin/api';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

function AdminConfirmationDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  error = '',
  success = '',
  onClose,
  onConfirm,
}) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          {error ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'error.50',
                color: 'error.main',
                border: '1px solid',
                borderColor: 'error.200',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          ) : null}

          {success ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'success.50',
                color: 'success.main',
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Typography variant="body2">{success}</Typography>
            </Box>
          ) : null}

          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Processing request...</Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading} variant="text">
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="outlined">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatTimestamp(value) {
  if (!value) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Date(num * 1000).toLocaleString();
}

export default function QueueJobsListPage() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const section = useMemo(() => {
    if (pathname.includes('/reserved')) return 'reserved';
    if (pathname.includes('/failed')) return 'failed';
    return 'queued';
  }, [pathname]);

  const items = useAsyncState([]);
  const [filters, setFilters] = useState({
    page: 1,
    page_size: 50,
    queue: '',
    priority: '',
    status: '',
    id: '',
  });

  const [pageMeta, setPageMeta] = useState({
    current_page: 1,
    total_pages: 1,
    total_size: 0,
    previous_page: null,
    next_page: null,
    page_size: 10,
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const route = useMemo(() => {
    if (section === 'queued') return '/ops/queues/queued';
    if (section === 'reserved') return '/ops/queues/reserved';
    return '/ops/queues/failed';
  }, [section]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const isRefresh = !initialLoading && Boolean(items.state.data?.length);
      if (isRefresh) setRefreshing(true);
      else items.setLoading();

      try {
        const response = await fetchAdmin(route, {
          page: filters.page,
          page_size: filters.page_size,
          queue: filters.queue,
          priority: filters.priority,
          status: filters.status,
          id: filters.id,
        });

        if (!active) return;
        items.setData(response.data || []);
        setPageMeta({
          current_page: response.current_page || 1,
          total_pages: response.total_pages || 1,
          total_size: response.total_size || 0,
          previous_page: response.previous_page || null,
          next_page: response.next_page || null,
          page_size: response.page_size || filters.page_size,
        });
      } catch (error) {
        if (!active) return;
        items.setError(toErrorMessage(error));
      } finally {
        if (!active) return;
        setInitialLoading(false);
        setRefreshing(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [route, filters.page, filters.page_size, filters.queue, filters.priority, filters.status, filters.id]);

  const rows = useMemo(() => {
    return (items.state.data || []).map((row) => ({
      id: row.id,
      queue: row.queue || row.queue_name || '—',
      payload: row.payload ? safeJsonParse(row.payload, {}) : {},
      priority: row.priority || '—',
      status: row.status === 1 ? 'reserved' : row.status === 0 ? 'queued' : 'failed',
      attempts: row.attempts ?? 0,
      availableAt: formatTimestamp(row.available_at),
      createdAt: formatTimestamp(row.created_at),
      reservedAt: formatTimestamp(row.reserved_at),
      raw: row,
    }));
  }, [items.state.data]);

  const closeActionDialog = () => {
    if (actionLoading) return;
    setPendingAction(null);
    setActionError('');
    setActionSuccess('');
  };

  const runAction = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await postAdmin(pendingAction.path);
      setActionSuccess(pendingAction.success);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });

      const response = await fetchAdmin(route, {
        page: filters.page,
        page_size: filters.page_size,
        queue: filters.queue,
        priority: filters.priority,
        status: filters.status,
        id: filters.id,
      });

      items.setData(response.data || []);
      setPageMeta({
        current_page: response.current_page || 1,
        total_pages: response.total_pages || 1,
        total_size: response.total_size || 0,
        previous_page: response.previous_page || null,
        next_page: response.next_page || null,
        page_size: response.page_size || filters.page_size,
      });
    } catch (error) {
      const message = toErrorMessage(error);
      setActionError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(false);
      closeActionDialog();
    }
  };

  return (
    <AdminRouteLayout title={`${section.charAt(0).toUpperCase() + section.slice(1)} Jobs`} breadcrumbTitle="Queue & Jobs">
      <Stack spacing={2.5}>

        <RequestState loading={items.state.loading} error={items.state.error}>
          <Paper variant="outlined" sx={{ position: 'relative', overflow: 'hidden' }}>
            {initialLoading ? (
              <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)' }}>
                  <Table stickyHeader size="medium" aria-label="queue jobs table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Queue</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Payload</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Attempts</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id} hover sx={{ '& td': { py: 1.5 } }}>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.queue}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, maxWidth: 320, wordBreak: 'break-word' }}>
                            {JSON.stringify(row.payload)}
                          </TableCell>
                          <TableCell>{row.priority}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell>{row.attempts}</TableCell>
                          <TableCell>{row.createdAt}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {section === 'failed' ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    setPendingAction({
                                      path: `/ops/queues/failed/${row.id}/retry`,
                                      success: 'Failed job retry accepted',
                                    })
                                  }
                                >
                                  Retry
                                </Button>
                              ) : null}
                              {section === 'queued' ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    setPendingAction({
                                      path: `/ops/queues/queued/${row.id}/cancel`,
                                      success: 'Queued job cancelled',
                                    })
                                  }
                                >
                                  Cancel
                                </Button>
                              ) : null}
                              {section === 'reserved' ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    setPendingAction({
                                      path: `/ops/queues/reserved/${row.id}/release`,
                                      success: 'Reserved job released',
                                    })
                                  }
                                >
                                  Release
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!rows.length && !items.state.loading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 8, textAlign: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              No jobs
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The selected queue endpoint returned no job rows.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>

                {refreshing ? (
                  <Backdrop
                    open
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      bgcolor: 'rgba(255,255,255,0.35)',
                      color: 'text.primary',
                    }}
                  >
                    <CircularProgress />
                  </Backdrop>
                ) : null}

                <TablePagination
                  component="div"
                  count={pageMeta.total_size}
                  page={Math.max(pageMeta.current_page - 1, 0)}
                  onPageChange={(_, nextPage) => setFilters((prev) => ({ ...prev, page: nextPage + 1 }))}
                  rowsPerPage={filters.page_size}
                  onRowsPerPageChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      page: 1,
                      page_size: parseInt(event.target.value, 10),
                    }))
                  }
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Box>
            )}
          </Paper>
        </RequestState>
      </Stack>

      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm queue action"
        description="This action will be applied directly to the selected queue job."
        confirmLabel="Run action"
        loading={actionLoading}
        error={actionError}
        success={actionSuccess}
        onClose={closeActionDialog}
        onConfirm={runAction}
      />
    </AdminRouteLayout>
  );
}